import { useAuthStore } from '@/store/useAuthStore';
import { ApiError, parseErrorBody, userMessageFor } from './errors';
import { newCorrelationId, reportRequestFailure } from '@/lib/log/clientLog';

/**
 * The app's single HTTP pipeline (port of hp-app-admin-react's client,
 * cookie mode only — SREG is browser-only):
 *
 *   - HttpOnly `access_token` cookie + readable `csrf_token` echoed as
 *     X-CSRF-Token on every call (double submit; sreg-api and the BFF both check it)
 *   - single-flight /auth/refresh on 401 or CSRF 403, then one retry
 *   - failures reject with a typed ApiError; whether to show it is the
 *     query client's job (app/queryClient.ts), not the client's
 *
 * Two bases on one origin: `api` -> /api (sreg-api), `bff` -> /bff.
 */

export const CSRF_COOKIE_NAME = 'csrf_token';

// sreg-api's shared x-api-key. Local dev bakes it in (VITE_X_API_KEY); a
// deployed build leaves it empty and reads it from the BFF at runtime, so one
// image serves every environment (promotion by digest).
let xApiKey: string = import.meta.env.VITE_X_API_KEY || '';
let configLoad: Promise<void> | null = null;

/** Loads runtime config once (no-op when the key was baked in). */
export function loadRuntimeConfig(): Promise<void> {
  if (xApiKey) return Promise.resolve();
  configLoad ??= fetch('/bff/config', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : {}))
    .then((c: { xApiKey?: string }) => {
      xApiKey = c.xApiKey ?? '';
    })
    .catch(() => {
      configLoad = null; // retry on the next call
    });
  return configLoad;
}

/** Test hook. */
export function _setApiKeyForTest(key: string) {
  xApiKey = key;
  configLoad = null;
}

const csrfCookieRegex = /(?:^|;\s*)csrf_token=([^;]*)/;

export function getCsrfToken(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(csrfCookieRegex);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

/** Headers for any same-origin call (also used by the chat transport). */
export function sessionHeaders(json = false): Record<string, string> {
  const h: Record<string, string> = { 'X-Session-Mode': 'cookie' };
  if (json) h['Content-Type'] = 'application/json';
  const csrf = getCsrfToken();
  if (csrf) h['X-CSRF-Token'] = csrf;
  if (xApiKey) h['X-Api-Key'] = xApiKey;
  return h;
}

// --- refresh ---------------------------------------------------------------

let refreshInProgress: Promise<boolean> | null = null;
let logoutInProgress = false;

export function setLogoutInProgress(value: boolean) {
  logoutInProgress = value;
}

/** Single-flight session refresh. Resolves true when a new session cookie was set. */
export function refreshSession(): Promise<boolean> {
  if (refreshInProgress) return refreshInProgress;
  refreshInProgress = (async () => {
    try {
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: sessionHeaders(true),
      });
      return r.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInProgress = null;
  });
  return refreshInProgress;
}

async function onUnrecoverableAuth(): Promise<void> {
  if (logoutInProgress) return;
  const { signOutLocally } = await import('@/services/authService');
  signOutLocally();
}

// --- request pipeline ------------------------------------------------------

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
}

const AUTH_EXEMPT = ['/auth/refresh', '/auth/logout', '/login'];

async function request<T>(
  base: '/api' | '/bff',
  endpoint: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  await loadRuntimeConfig();
  const method = options.method ?? 'GET';
  const url = `${base}${endpoint}${buildQuery(options.query)}`;
  // One id per call, sent as CorrelationID: sreg-api and the BFF log under it
  // and return it in error bodies, so the error banner's "Reference" matches
  // every log line for this request.
  const correlationId = newCorrelationId();
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers: { ...sessionHeaders(options.body !== undefined), CorrelationID: correlationId },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (err) {
    if (!(err instanceof DOMException && err.name === 'AbortError')) {
      reportRequestFailure({ method, path: `${base}${endpoint}`, correlationId, error: err });
    }
    throw err;
  }

  if (!response.ok) {
    const rawBody = await response.text();
    const parsed = parseErrorBody(response.status, rawBody);
    const exempt = AUTH_EXEMPT.some((p) => endpoint.startsWith(p));
    const csrfFailure = response.status === 403 && /csrf/i.test(rawBody);

    if ((response.status === 401 || csrfFailure) && !isRetry && !exempt && !logoutInProgress) {
      if (await refreshSession()) return request<T>(base, endpoint, options, true);
      if (useAuthStore.getState().isAuthenticated) await onUnrecoverableAuth();
    }

    reportRequestFailure({
      method,
      path: `${base}${endpoint}`,
      status: response.status,
      correlationId: parsed.correlationId ?? correlationId,
    });
    throw new ApiError({
      status: response.status,
      code: parsed.code,
      correlationId: parsed.correlationId ?? correlationId,
      userMessage: userMessageFor(response.status, parsed),
      endpoint,
      method,
      rawBody,
    });
  }

  const text = await response.text();
  if (response.status === 204 || !text) return undefined as T;
  return JSON.parse(text) as T;
}

function verbs(base: '/api' | '/bff') {
  return {
    get: <T>(endpoint: string, query?: QueryParams, signal?: AbortSignal) =>
      request<T>(base, endpoint, { method: 'GET', query, signal }),
    post: <T>(endpoint: string, body?: unknown) => request<T>(base, endpoint, { method: 'POST', body: body ?? {} }),
    put: <T>(endpoint: string, body?: unknown, query?: QueryParams) =>
      request<T>(base, endpoint, { method: 'PUT', body: body ?? {}, query }),
    patch: <T>(endpoint: string, body: unknown) => request<T>(base, endpoint, { method: 'PATCH', body }),
    del: <T>(endpoint: string) => request<T>(base, endpoint, { method: 'DELETE' }),
  };
}

/** sreg-api (through the LB / dev proxy at /api). */
export const api = verbs('/api');
/** The FastAPI BFF (/bff). */
export const bff = verbs('/bff');
